import { motion } from "framer-motion";
import { useState } from "react";
import {
  Search,
  HelpCircle,
  MessageSquare,
  Mail,
  ChevronDown,
  ChevronUp,
  BookOpen,
  ShieldCheck,
} from "lucide-react";
import ScreenHeader from "../components/ScreenHeader";

const FAQS = [
  {
    q: "Is Shwiip a bank?",
    a: "No. Shwiip is not a bank, EMI, or wallet. Shwiip is a technical interface that routes your payment to a licensed partner — Finshark — which executes the transfer.",
  },
  {
    q: "Where is my money?",
    a: "Your money is never held by Shwiip. Funds move directly from your bank to the recipient's account via Finshark's regulated rails.",
  },
  {
    q: "How long do transfers take?",
    a: "Most domestic EU transfers complete in under 30 seconds when your bank supports instant payments. Cross-border may take a few minutes.",
  },
  {
    q: "What are the fees?",
    a: "Fees are displayed upfront on the Amount screen. They cover partner processing costs. You always see exactly what the recipient will receive before confirming.",
  },
  {
    q: "My payment failed — was I charged?",
    a: "No. If a payment fails, no funds are moved and no fees are charged. You can retry immediately from the Failure screen.",
  },
  {
    q: "Can I cancel a payment?",
    a: "Payments can be cancelled before you authorise them. Once executed by Finshark, they cannot be reversed. Contact support for disputes.",
  },
  {
    q: "How is my data protected?",
    a: "Shwiip never stores your banking credentials. Authentication happens inside your banking app or device via BankID, biometrics, or your PIN.",
  },
];

export const HelpScreen = () => {
  const [query, setQuery] = useState("");
  const [openIdx, setOpenIdx] = useState(0);

  const filtered = FAQS.filter(
    (f) =>
      !query.trim() ||
      f.q.toLowerCase().includes(query.toLowerCase()) ||
      f.a.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <motion.div
      initial={{ x: 20, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: -20, opacity: 0 }}
      transition={{ type: "spring", bounce: 0, duration: 0.3 }}
      className="h-full flex flex-col"
      data-testid="help-screen"
    >
      <ScreenHeader title="Help center" testId="help-back-btn" />

      <div className="flex-1 overflow-y-auto phone-scroll px-6 pb-10">
        <h1 className="font-display text-[26px] font-bold tracking-tight">
          How can we help?
        </h1>
        <p className="text-[13px] text-gray-500 mt-1.5">
          Answers, guides, and a direct line to our team.
        </p>

        <div className="relative mt-4">
          <Search
            className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
            strokeWidth={2.5}
          />
          <input
            data-testid="help-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search help articles"
            className="w-full bg-gray-100 rounded-full h-11 pl-11 pr-5 font-medium outline-none text-sm"
          />
        </div>

        {/* Contact cards */}
        <div className="mt-5 grid grid-cols-2 gap-3">
          <ContactCard
            icon={MessageSquare}
            title="Live chat"
            subtitle="Avg. 2 min"
            testId="help-chat"
            tone="dark"
          />
          <ContactCard
            icon={Mail}
            title="Email us"
            subtitle="support@shwiip.app"
            testId="help-email"
          />
        </div>

        {/* Guides */}
        <div className="mt-6">
          <div className="text-[11px] uppercase tracking-widest font-bold text-gray-400 mb-2">
            Quick guides
          </div>
          <GuideRow
            icon={BookOpen}
            title="How to send your first payment"
            testId="guide-send"
          />
          <GuideRow
            icon={ShieldCheck}
            title="Understanding partner attribution"
            testId="guide-compliance"
          />
          <GuideRow
            icon={HelpCircle}
            title="What to do if a transfer fails"
            testId="guide-failure"
          />
        </div>

        {/* FAQs */}
        <div className="mt-6">
          <div className="text-[11px] uppercase tracking-widest font-bold text-gray-400 mb-2">
            Frequently asked
          </div>
          <div className="rounded-2xl border border-gray-100 overflow-hidden bg-white">
            {filtered.map((f, i) => {
              const open = i === openIdx;
              return (
                <div
                  key={f.q}
                  data-testid={`faq-${i}`}
                  className={i === filtered.length - 1 ? "" : "border-b border-gray-100"}
                >
                  <button
                    onClick={() => setOpenIdx(open ? -1 : i)}
                    className="w-full flex items-center justify-between px-4 py-3.5 text-left hover:bg-gray-50 transition-colors gap-3"
                  >
                    <span className="text-[13px] font-semibold text-gray-900 flex-1">
                      {f.q}
                    </span>
                    {open ? (
                      <ChevronUp className="w-4 h-4 text-gray-500 flex-shrink-0" strokeWidth={2.2} />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" strokeWidth={2.2} />
                    )}
                  </button>
                  {open && (
                    <div className="px-4 pb-4 text-[12px] text-gray-600 leading-snug">
                      {f.a}
                    </div>
                  )}
                </div>
              );
            })}
            {filtered.length === 0 && (
              <div className="px-4 py-8 text-center text-[12px] text-gray-500">
                No articles match "{query}"
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const ContactCard = ({ icon: Icon, title, subtitle, tone, testId }) => {
  const dark = tone === "dark";
  return (
    <button
      data-testid={testId}
      className={
        "rounded-2xl p-4 text-left transition-transform active:scale-[0.98] " +
        (dark
          ? "bg-gray-900 text-white hover:bg-black"
          : "bg-gray-50 border border-gray-100 hover:bg-gray-100 text-gray-900")
      }
    >
      <div
        className={
          "w-9 h-9 rounded-full flex items-center justify-center mb-3 " +
          (dark ? "bg-white/10" : "bg-white border border-gray-200")
        }
      >
        <Icon className="w-4 h-4" strokeWidth={2.2} />
      </div>
      <div className="font-semibold text-[13px]">{title}</div>
      <div
        className={
          "text-[11px] mt-0.5 " + (dark ? "text-gray-400" : "text-gray-500")
        }
      >
        {subtitle}
      </div>
    </button>
  );
};

const GuideRow = ({ icon: Icon, title, testId }) => (
  <button
    data-testid={testId}
    className="w-full flex items-center gap-3 py-3 px-2 -mx-2 rounded-xl hover:bg-gray-50 transition-colors text-left"
  >
    <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
      <Icon className="w-4 h-4 text-gray-900" strokeWidth={2.2} />
    </div>
    <span className="flex-1 text-[13px] font-semibold text-gray-900">{title}</span>
  </button>
);

export default HelpScreen;
